import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import JobRow from "@/components/JobRow";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import Pagination from "@/components/Pagination";
import usePaginationLinks from "@/hooks/usePaginationLinks";
import NotificationAlarm from "@/components/widgets/NotificationAlarm";

const ANY = "__any__";
const PAGE_SIZE = 30;

export default function JobList({ pageTitle = "All India Jobs", forceLatest = false, basePath = "/all-india-jobs" }) {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState({ departments: [], states: [], qualifications: [], job_types: [] });
  const [loading, setLoading] = useState(true);

  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const filters = {
    search: params.get("search") || "",
    department: params.get("department") || "",
    state: params.get("state") || "",
    qualification: params.get("qualification") || "",
    job_type: params.get("job_type") || "",
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.append(k, v));
    q.append("sort", forceLatest ? "latest" : "latest");
    q.append("limit", String(PAGE_SIZE));
    q.append("skip", String((page - 1) * PAGE_SIZE));
    const { data } = await api.get(`/jobs?${q}`);
    setItems(data.items); setTotal(data.total); setLoading(false);
  }, [params, forceLatest]); // eslint-disable-line

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Per-page SEO: title, meta description, canonical
  useEffect(() => {
    const SITE = "https://hireds.in";
    const oldTitle = document.title;
    const oldDescEl = document.querySelector('meta[name="description"]');
    const oldDesc = oldDescEl?.content;
    const oldCanonEl = document.querySelector('link[rel="canonical"]');
    const oldCanon = oldCanonEl?.href;
    document.title = `${pageTitle} 2026 — Latest Indian Govt Job Notifications | hireds.in`;
    if (oldDescEl) oldDescEl.content = `Browse all latest Indian government job notifications on hireds.in — ${pageTitle.toLowerCase()} updated daily with eligibility, last date, vacancies and official apply links. Curated by Dileshwar Vitthale.`.slice(0, 160);
    if (oldCanonEl) oldCanonEl.href = `${SITE}${basePath}`;
    return () => {
      document.title = oldTitle;
      if (oldDescEl && oldDesc) oldDescEl.content = oldDesc;
      if (oldCanonEl && oldCanon) oldCanonEl.href = oldCanon;
    };
  }, [pageTitle, basePath]);

  // Inject <link rel="prev"/"next"> for SEO pagination signals
  usePaginationLinks(basePath, Object.fromEntries(params), page, totalPages);

  const update = (k, v) => {
    const np = new URLSearchParams(params);
    const val = v === ANY ? "" : v;
    if (val) np.set(k, val); else np.delete(k);
    np.delete("page"); // reset to first page on filter change
    setParams(np);
  };

  const goToPage = (p) => {
    const np = new URLSearchParams(params);
    if (p > 1) np.set("page", String(p)); else np.delete("page");
    setParams(np);
  };

  const clear = () => setParams(new URLSearchParams());

  const FilterPanel = () => (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">Department</div>
        <Select value={filters.department || ANY} onValueChange={(v) => update("department", v)}>
          <SelectTrigger data-testid="filter-department"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>Any</SelectItem>{cats.departments.map((d)=>(<SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">State</div>
        <Select value={filters.state || ANY} onValueChange={(v) => update("state", v)}>
          <SelectTrigger data-testid="filter-state"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>Any</SelectItem>{cats.states.map((s)=>(<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">Qualification</div>
        <Select value={filters.qualification || ANY} onValueChange={(v) => update("qualification", v)}>
          <SelectTrigger data-testid="filter-qualification"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>Any</SelectItem>{cats.qualifications.map((q)=>(<SelectItem key={q} value={q}>{q}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">Job Type</div>
        <Select value={filters.job_type || ANY} onValueChange={(v) => update("job_type", v)}>
          <SelectTrigger data-testid="filter-jobtype"><SelectValue placeholder="Any"/></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>Any</SelectItem>{cats.job_types.map((t)=>(<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <Button data-testid="clear-filters" onClick={clear} variant="outline" className="w-full">Clear All Filters</Button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="joblist-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{pageTitle}</h1>
        <p className="text-slate-600 text-sm mt-1">{total} active vacancies — updated daily.</p>
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input data-testid="joblist-search" defaultValue={filters.search} onKeyDown={(e)=>{ if(e.key==='Enter') update('search', e.currentTarget.value); }} placeholder="Search by title, organization..." className="pl-9"/>
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="open-filters-mobile" variant="outline" className="md:hidden"><Filter className="w-4 h-4 mr-2"/>Filters</Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 overflow-y-auto">
            <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
            <div className="mt-4"><FilterPanel/></div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6">
        <aside className="hidden md:block">
          <div className="sticky top-20 bg-white border border-slate-200 rounded-lg p-4">
            <div className="font-bold mb-3 text-slate-900">Filters</div>
            <FilterPanel/>
          </div>
        </aside>
        <div className="space-y-3">
          {forceLatest && <NotificationAlarm />}
          {loading && <div className="text-slate-500" data-testid="jobs-loading">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-8 md:p-10" data-testid="no-jobs">
              <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-2" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>
                No vacancies match the current filters
              </h2>
              <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                This combination of filters has no live notifications today. New Sarkari notifications are added every day at 06:00 IST — check back tomorrow, or browse one of the popular categories below.
              </p>
              <div className="mt-5">
                <Button data-testid="empty-clear-filters" onClick={clear} className="bg-orange-600 hover:bg-orange-700 text-white h-11">
                  Reset all filters
                </Button>
              </div>
              <div className="mt-7">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Browse by sector</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { slug: "railway", label: "Railway Jobs" },
                    { slug: "banking", label: "Bank Jobs" },
                    { slug: "ssc", label: "SSC Jobs" },
                    { slug: "upsc", label: "UPSC Jobs" },
                    { slug: "defense", label: "Defense Jobs" },
                    { slug: "teaching", label: "Teaching Jobs" },
                    { slug: "state-govt", label: "State Govt Jobs" },
                    { slug: "psu", label: "PSU Jobs" },
                  ].map((s) => (
                    <a
                      key={s.slug}
                      href={`/sectors/${s.slug}`}
                      data-testid={`empty-sector-${s.slug}`}
                      className="inline-flex items-center min-h-11 px-4 py-2 rounded-full bg-slate-100 hover:bg-orange-100 hover:text-orange-700 text-slate-700 text-sm font-semibold transition-colors border border-slate-200"
                    >
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
              <div className="mt-7">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">Or explore</div>
                <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                  <li><a href="/latest-jobs" className="text-orange-600 hover:underline">Latest Sarkari Jobs (all India)</a></li>
                  <li><a href="/admit-cards" className="text-orange-600 hover:underline">Admit Cards released this week</a></li>
                  <li><a href="/results" className="text-orange-600 hover:underline">Recently declared Results</a></li>
                  <li><a href="/blog" className="text-orange-600 hover:underline">Exam-prep guides &amp; career articles</a></li>
                </ul>
              </div>
            </div>
          )}
          {items.map((j, i) => <JobRow key={j.id} job={j} idx={i}/>)}
          <Pagination page={page} totalPages={totalPages} onPageChange={goToPage} />
        </div>
      </div>
    </div>
  );
}
